import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {OttopilotEventTableViewColumn} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTableViewColumn";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_LEADS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EmotoradEventTypes,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

type BuildEmotradTableColumnsParams = {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
};

function buildTableViewColumnsForIds(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
    visibleColumnIds: Array<string>;
}): Array<OttopilotEventTableViewColumn> {
    const {eventTemplate, journeysModuleConfiguration, visibleColumnIds} = params;

    const eventColumnsById = new Map(
        getAdditionalColumns(eventTemplate).map((column) => [column.id, column]),
    );
    const journeyColumnsById = new Map(
        getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.id, column]),
    );

    return visibleColumnIds.map((columnId): OttopilotEventTableViewColumn => {
        const eventAdditionalColumn = eventColumnsById.get(columnId as Uuid);
        if (eventAdditionalColumn !== undefined) {
            return {
                id: eventAdditionalColumn.id,
                field: eventAdditionalColumn.id,
                displayName: eventAdditionalColumn.displayName,
                sortingCapability: true,
                filteringCapability_contains: true,
                isAdditionalColumn: true,
                columnType: "OttopilotEvent",
                type: eventAdditionalColumn.type,
            };
        }

        const journeyAdditionalColumn = journeyColumnsById.get(columnId as Uuid);
        if (journeyAdditionalColumn !== undefined) {
            return {
                id: journeyAdditionalColumn.id,
                field: journeyAdditionalColumn.id,
                displayName: journeyAdditionalColumn.displayName,
                sortingCapability: true,
                filteringCapability_contains: true,
                isAdditionalColumn: true,
                columnType: "OttopilotEventContainer",
                type: journeyAdditionalColumn.type,
            };
        }

        if (
            columnId === "createdAt"
            || columnId === "createdBy"
            || columnId === "updatedAt"
            || columnId === "updatedBy"
        ) {
            return {
                id: columnId as Uuid,
                field: columnId,
                displayName: columnId,
                sortingCapability: true,
                filteringCapability_contains: true,
                isAdditionalColumn: false,
                columnType: "OttopilotEvent",
            };
        }

        return {
            id: columnId as Uuid,
            field: columnId,
            displayName: columnId,
            sortingCapability: true,
            filteringCapability_contains: true,
            isAdditionalColumn: false,
            columnType: "OttopilotEvent",
        };
    });
}

function buildEmotradTableColumns(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
    eventType: Uuid;
    visibleColumnIds: Array<string>;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration, eventType, visibleColumnIds} = params;

    if (eventTemplate.id !== eventType) {
        return eventTemplate;
    }

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                tableViewColumns: buildTableViewColumnsForIds(getAdditionalColumns, {
                    eventTemplate: eventTemplate,
                    journeysModuleConfiguration: journeysModuleConfiguration,
                    visibleColumnIds: visibleColumnIds,
                }),
            },
        },
    };
}

export function buildEmotradTestrideTableColumns(getAdditionalColumns: CapabilityUdoColumnResolver, params: BuildEmotradTableColumnsParams): OttopilotEventTemplate {
    return buildEmotradTableColumns(getAdditionalColumns, {
        eventTemplate: params.eventTemplate,
        journeysModuleConfiguration: params.journeysModuleConfiguration,
        eventType: EmotoradEventTypes.testride as Uuid,
        visibleColumnIds: EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    });
}

export function buildEmotradSalesTableColumns(getAdditionalColumns: CapabilityUdoColumnResolver, params: BuildEmotradTableColumnsParams): OttopilotEventTemplate {
    return buildEmotradTableColumns(getAdditionalColumns, {
        eventTemplate: params.eventTemplate,
        journeysModuleConfiguration: params.journeysModuleConfiguration,
        eventType: EmotoradEventTypes.sales as Uuid,
        visibleColumnIds: EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    });
}

export function buildEmotradCallTableColumns(getAdditionalColumns: CapabilityUdoColumnResolver, params: BuildEmotradTableColumnsParams): OttopilotEventTemplate {
    return buildEmotradTableColumns(getAdditionalColumns, {
        eventTemplate: params.eventTemplate,
        journeysModuleConfiguration: params.journeysModuleConfiguration,
        eventType: EmotoradEventTypes.call as Uuid,
        visibleColumnIds: EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    });
}

export function buildEmotradWalkinTableColumns(getAdditionalColumns: CapabilityUdoColumnResolver, params: BuildEmotradTableColumnsParams): OttopilotEventTemplate {
    return buildEmotradTableColumns(getAdditionalColumns, {
        eventTemplate: params.eventTemplate,
        journeysModuleConfiguration: params.journeysModuleConfiguration,
        eventType: EmotoradEventTypes.walkin as Uuid,
        visibleColumnIds: EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    });
}

export function buildEmotradLeadTableColumns(getAdditionalColumns: CapabilityUdoColumnResolver, params: BuildEmotradTableColumnsParams): OttopilotEventTemplate {
    return buildEmotradTableColumns(getAdditionalColumns, {
        eventTemplate: params.eventTemplate,
        journeysModuleConfiguration: params.journeysModuleConfiguration,
        eventType: EmotoradEventTypes.lead as Uuid,
        visibleColumnIds: EMOTORAD_LEADS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    });
}
